import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getProfileImageUrl } from '@/lib/config';
import { adminService, UpdateProfileData, UpdatePasswordData } from '@/services/admin.service';
import { authService } from '@/services/auth.service';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Layout } from '@/components/Layout';
import { User, Lock, Phone, Mail, Key, Camera, X, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { Avatar } from '@/components/Avatar';

export function MyProfilePage() {
  const [profileData, setProfileData] = useState<UpdateProfileData>({
    name: '',
    mobile: '',
  });
  const [passwordData, setPasswordData] = useState<UpdatePasswordData>({
    currentPassword: '',
    newPassword: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [removeProfileImage, setRemoveProfileImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const { updateUser } = useAuth();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const data = await adminService.getMyProfile();
      return data;
    },
  });

  useEffect(() => {
    if (profile) {
      setProfileData({
        name: profile.name || '',
        mobile: profile.mobile || '',
      });
      if (profile.profileImage) {
        setProfileImagePreview(getProfileImageUrl(profile.profileImage) || null);
      } else {
        // Clear preview if profile image is removed
        setProfileImagePreview(null);
      }
      // Reset removal flag when profile is loaded
      setRemoveProfileImage(false);
    }
  }, [profile]);

  const updateProfileMutation = useMutation({
    mutationFn: (data: { profileData: UpdateProfileData; imageFile?: File; removeImage?: boolean }) =>
      adminService.updateProfile(data.profileData, data.imageFile, data.removeImage),
    onSuccess: async (updatedProfile) => {
      await queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      // Update the user in AuthContext with the new profile data
      updateUser({
        name: updatedProfile.name,
        mobile: updatedProfile.mobile,
        profileImage: updatedProfile.profileImage || undefined,
      });
      setSuccessMessage('Profile updated successfully');
      setShowSuccessModal(true);
      setError('');
      setProfileImageFile(null);
      setProfileImagePreview(null);
      setRemoveProfileImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    onError: (error: any) => {
      console.error('Error updating profile:', error);
      setError(error.response?.data?.message || 'Failed to update profile');
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: adminService.updatePassword,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      setSuccessMessage('Password updated successfully');
      setShowSuccessModal(true);
      setPasswordData({ currentPassword: '', newPassword: '' });
      setConfirmPassword('');
      setShowPasswordSection(false);
      setError('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    },
    onError: (error: any) => {
      console.error('Error updating password:', error);
      setError(error.response?.data?.message || 'Failed to update password');
    },
  });

  const sendResetEmailMutation = useMutation({
    mutationFn: () => authService.forgotPassword(profile?.email || ''),
    onSuccess: () => {
      setSuccessMessage('Password reset link has been sent to your email');
      setShowSuccessModal(true);
      setShowPasswordSection(false);
      setError('');
    },
    onError: (error: any) => {
      console.error('Error sending reset email:', error);
      setError(error.response?.data?.message || 'Failed to send reset email');
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Only send fields that have changed
    const updateData: UpdateProfileData = {};
    if (profileData.name && profileData.name !== profile?.name) {
      updateData.name = profileData.name;
    }
    if (profileData.mobile !== profile?.mobile) {
      updateData.mobile = profileData.mobile || '';
    }

    // Check if there are any changes
    if (Object.keys(updateData).length === 0 && !profileImageFile && !removeProfileImage) {
      setError('No changes to save');
      return;
    }

    updateProfileMutation.mutate({
      profileData: updateData,
      imageFile: profileImageFile || undefined,
      removeImage: removeProfileImage,
    });
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.match(/^image\/(jpg|jpeg|png|gif|webp)$/)) {
        setError('Please select a valid image file (jpg, jpeg, png, gif, or webp)');
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setProfileImageFile(file);
      setRemoveProfileImage(false); // Clear removal flag when selecting new image
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleRemoveImage = () => {
    setProfileImageFile(null);
    setProfileImagePreview(null);
    setRemoveProfileImage(true); // Mark image for removal
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Password validation functions
  const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
    if (password.length === 0) return 'weak';
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  };

  const isPasswordValid = (password: string): boolean => {
    return (
      password.length >= 8 &&
      /\d/.test(password) &&
      /[^a-zA-Z0-9]/.test(password)
    );
  };

  const isFormValid = (): boolean => {
    return (
      (passwordData.currentPassword?.length ?? 0) > 0 &&
      passwordData.newPassword.length > 0 &&
      confirmPassword.length > 0 &&
      passwordData.newPassword === confirmPassword &&
      isPasswordValid(passwordData.newPassword)
    );
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (!passwordData.currentPassword) {
      setError('Current password is required');
      return;
    }

    if (!isPasswordValid(passwordData.newPassword)) {
      setError('New password must be at least 8 characters, include a number and special character');
      return;
    }

    if (passwordData.newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    const currentPassword = passwordData.currentPassword ?? '';
    updatePasswordMutation.mutate({
      currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  const handleForgotPassword = () => {
    setShowConfirmModal(true);
  };

  const confirmSendResetEmail = () => {
    sendResetEmailMutation.mutate();
    setShowConfirmModal(false);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 max-w-2xl mx-auto w-full">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground">My Profile</h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
            Update your profile information and password
          </p>
        </div>

        {/* Profile Information Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Profile Information</CardTitle>
            <CardDescription className="text-xs">Update your name and mobile number</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <form onSubmit={handleProfileSubmit} className="space-y-3">
              {error && !showPasswordSection && (
                <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
                  {error}
                </div>
              )}

              {/* Profile Image */}
              <div className="space-y-1">
                <label className="text-xs font-medium">Profile Image</label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="relative">
                      <Avatar
                        imageUrl={profileImagePreview || profile?.profileImage}
                        name={profileData.name || profile?.name}
                        size="lg"
                        alt="Profile"
                      />
                      {profileImagePreview && (
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-10"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="profileImage"
                    />
                    <label
                      htmlFor="profileImage"
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 cursor-pointer"
                    >
                      <Camera className="h-3.5 w-3.5" />
                      {profileImageFile ? 'Change Image' : 'Upload Image'}
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">Max 5MB. JPG, PNG, GIF, or WEBP</p>
                  </div>
                </div>
              </div>

              {/* Email (read-only) */}
              <div className="space-y-1">
                <label htmlFor="email" className="text-xs font-medium">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="pl-9 h-9 text-sm bg-muted"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>

              {/* Name */}
              <div className="space-y-1">
                <label htmlFor="name" className="text-xs font-medium">
                  Name
                </label>
                <div className="relative">
                  <User className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    id="name"
                    placeholder="Enter your name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className="pl-9 h-9 text-sm"
                    required
                  />
                </div>
              </div>

              {/* Mobile */}
              <div className="space-y-1">
                <label htmlFor="mobile" className="text-xs font-medium">
                  Mobile
                </label>
                <div className="relative">
                  <Phone className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    id="mobile"
                    placeholder="Enter your mobile number (optional)"
                    value={profileData.mobile}
                    onChange={(e) => setProfileData({ ...profileData, mobile: e.target.value })}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={updateProfileMutation.isPending}
                  className="flex-1"
                >
                  {updateProfileMutation.isPending ? 'Updating...' : 'Update Profile'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Password Update Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Password</CardTitle>
            <CardDescription className="text-xs">Change your password</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {!showPasswordSection ? (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Click the button below to update your password. You'll need to enter your current password.
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() => setShowPasswordSection(true)}
                    className="flex-1 h-9 text-sm"
                    size="sm"
                  >
                    <Key className="mr-2 h-3.5 w-3.5" />
                    Update Password
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handlePasswordSubmit} className="space-y-3">
                {error && showPasswordSection && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
                    {error}
                  </div>
                )}

                {/* Current Password */}
                <div className="space-y-1">
                  <label htmlFor="currentPassword" className="text-xs font-medium">
                    Current Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter your current password"
                      value={passwordData.currentPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, currentPassword: e.target.value })
                      }
                      className="pl-9 pr-9 h-9 text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-1">
                  <label htmlFor="newPassword" className="text-xs font-medium">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, newPassword: e.target.value })
                      }
                      className="pl-9 pr-9 h-9 text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {/* Password Rules */}
                  <div className="mt-1.5 p-2 bg-muted/50 rounded-md border border-muted">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Password requirements:</p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      <li className={`flex items-center gap-1.5 ${passwordData.newPassword.length >= 8 ? 'text-green-600 dark:text-green-400' : ''}`}>
                        {passwordData.newPassword.length >= 8 ? (
                          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-3 w-3 flex-shrink-0" />
                        )}
                        At least 8 characters
                      </li>
                      <li className={`flex items-center gap-1.5 ${/\d/.test(passwordData.newPassword) ? 'text-green-600 dark:text-green-400' : ''}`}>
                        {/\d/.test(passwordData.newPassword) ? (
                          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-3 w-3 flex-shrink-0" />
                        )}
                        Include a number
                      </li>
                      <li className={`flex items-center gap-1.5 ${/[^a-zA-Z0-9]/.test(passwordData.newPassword) ? 'text-green-600 dark:text-green-400' : ''}`}>
                        {/[^a-zA-Z0-9]/.test(passwordData.newPassword) ? (
                          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-3 w-3 flex-shrink-0" />
                        )}
                        Include a special character
                      </li>
                    </ul>
                  </div>
                  {/* Password Strength Indicator */}
                  {passwordData.newPassword.length > 0 && (
                    <div className="mt-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Strength:</span>
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              getPasswordStrength(passwordData.newPassword) === 'weak'
                                ? 'bg-red-500 w-1/3'
                                : getPasswordStrength(passwordData.newPassword) === 'medium'
                                ? 'bg-yellow-500 w-2/3'
                                : 'bg-green-500 w-full'
                            }`}
                          />
                        </div>
                        <span
                          className={`text-xs font-medium ${
                            getPasswordStrength(passwordData.newPassword) === 'weak'
                              ? 'text-red-600 dark:text-red-400'
                              : getPasswordStrength(passwordData.newPassword) === 'medium'
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}
                        >
                          {getPasswordStrength(passwordData.newPassword).charAt(0).toUpperCase() + getPasswordStrength(passwordData.newPassword).slice(1)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm New Password */}
                <div className="space-y-1">
                  <label htmlFor="confirmNewPassword" className="text-xs font-medium">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      id="confirmNewPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`pl-9 pr-9 h-9 text-sm ${
                        confirmPassword.length > 0 && passwordData.newPassword !== confirmPassword
                          ? 'border-red-500 focus:border-red-500'
                          : confirmPassword.length > 0 && passwordData.newPassword === confirmPassword
                          ? 'border-green-500 focus:border-green-500'
                          : ''
                      }`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && (
                    <p className={`text-xs flex items-center gap-1 ${
                      passwordData.newPassword === confirmPassword
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {passwordData.newPassword === confirmPassword ? (
                        <>
                          <CheckCircle2 className="h-3 w-3" />
                          Passwords match
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3" />
                          Passwords do not match
                        </>
                      )}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    type="submit"
                    disabled={updatePasswordMutation.isPending || !isFormValid()}
                    className="flex-1 h-9 text-sm"
                    size="sm"
                  >
                    {updatePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 text-sm"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordData({ currentPassword: '', newPassword: '' });
                      setConfirmPassword('');
                      setError('');
                      setShowCurrentPassword(false);
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>

                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Don't remember your current password?
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full h-8 text-xs"
                    onClick={handleForgotPassword}
                    disabled={sendResetEmailMutation.isPending}
                  >
                    {sendResetEmailMutation.isPending
                      ? 'Sending...'
                      : 'Send Password Reset Link to Email'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Success</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">{successMessage}</p>
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccessMessage('');
                  }}
                >
                  OK
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Confirm Action</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Send password reset link to your email?
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </Button>
                <Button onClick={confirmSendResetEmail}>
                  Confirm
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
}
